/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { WorkflowRunState } from '@smartsheet-bridge/bridge-sdk/lib/http';
import {
  Spacer,
  DatePickerDialog,
  Select,
  OptionsType,
  DatePickerDate,
  SelectValueType,
} from '@smartsheet/lodestar-core';
import {
  CalendarIcon,
  PlayIcon,
  ToolbarRefreshIcon,
} from '@smartsheet/lodestar-icons';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import i18n from '../../../../../../i18n';
import { startWorkflowRun } from '../../../../../../services/api/WorkflowService/WorkflowService';
import {
  filterSelector,
  setDefaultFromTimestamp,
  setFilter,
  setUserSetTimestamp,
  userSetFromTimestampSelector,
  workflowNameSelector,
  WorkflowRunFilterOption,
} from '../../../../../../services/ducks/workflowDuck/workflowDuck';
import {
  RUNLOG_LIST_FILTER_HEIGHT,
  RUNLOG_LIST_FILTER_MARGIN_BOTTOM,
} from '../../../../../../utils/dimensions';
import {
  toDatePickerDate,
  toUnixTimestamp,
} from '../../../../../../utils/time';
import { Column } from '../../../../../common/Column';
import { IconButton } from '../../../../../common/IconButton';
const { t: translate } = i18n;

const runLogListHeaderStyle = css`
  justify-content: space-between;
  margin-bottom: ${RUNLOG_LIST_FILTER_MARGIN_BOTTOM}px;
  height: ${RUNLOG_LIST_FILTER_HEIGHT}px;
`;

export const FILTER_OPTIONS: OptionsType<WorkflowRunFilterOption> = [
  {
    label: translate('designer:runLog.all'),
    value: 'all',
    states: [
      WorkflowRunState.IN_PROGRESS.toString(),
      WorkflowRunState.AWAITING_USER_INPUT.toString(),
      WorkflowRunState.AWAITING_ADMIN_INPUT.toString(),
      WorkflowRunState.ON_HOLD.toString(),
      WorkflowRunState.STOPPED.toString(),
      WorkflowRunState.DONE.toString(),
    ],
  },
  {
    label: translate('designer:runLog.stopped'),
    value: 'stopped',
    states: [
      WorkflowRunState.AWAITING_ADMIN_INPUT.toString(),
      WorkflowRunState.STOPPED.toString(),
    ],
  },
  {
    label: translate('designer:runLog.runningFilter'),
    value: 'running',
    states: [
      WorkflowRunState.ON_HOLD.toString(),
      WorkflowRunState.AWAITING_USER_INPUT.toString(),
      WorkflowRunState.IN_PROGRESS.toString(),
    ],
  },
  {
    label: translate('designer:runLog.completed'),
    value: 'completed',
    states: [WorkflowRunState.DONE.toString()],
  },
];

export const ListHeader = () => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const datePickerButtonRef = useRef<HTMLDivElement>(null);
  const [isDateFilterDialogVisible, setIsDateFilterDialogVisible] =
    useState(false);

  const workflowName = useSelector(workflowNameSelector);

  const selectedFilterOption = useSelector(filterSelector);
  const userSetFromTimestamp = toDatePickerDate(
    useSelector(userSetFromTimestampSelector)
  );
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const today: DatePickerDate = toDatePickerDate(Date.now().toString())!;

  const toggleDateFilterDialogVisibility = () =>
    setIsDateFilterDialogVisible(!isDateFilterDialogVisible);

  const updateFilter = (option: SelectValueType<WorkflowRunFilterOption>) => {
    if (option) {
      dispatch(setFilter(option));
    }
  };

  const updateTimestamp = (date: DatePickerDate) => {
    if (date) {
      dispatch(setUserSetTimestamp(toUnixTimestamp(date)));
      toggleDateFilterDialogVisibility();
    }
  };

  const refreshHandler = () => {
    dispatch(setDefaultFromTimestamp(Date.now().toString()));
  };
  return (
    <Spacer orientation="row" space="small" css={runLogListHeaderStyle}>
      <Column width="70%">
        <Select
          css={css`
            width: 130px;
          `}
          options={FILTER_OPTIONS}
          onChange={updateFilter}
          isClearable={false}
          isSearchable={false}
          menuPortalTarget={null}
          value={selectedFilterOption}
          aria-label={translate('designer:runLog.filterByState')}
          name={translate('designer:runLog.filterByState')}
        ></Select>
        <div
          ref={datePickerButtonRef}
          css={css`
            position: relative;
          `}
        >
          <IconButton
            appearance={userSetFromTimestamp ? 'primary' : 'secondary'}
            iconBefore={<CalendarIcon />}
            onClick={toggleDateFilterDialogVisibility}
            aria-label={translate('designer:runLog.timestamp')}
          />
          <DatePickerDialog
            id="xx"
            css={css`
              position: absolute;
              top: 30px;
              right: -210px;
              z-index: 5;
            `}
            locale="en"
            isOpen={isDateFilterDialogVisible}
            targetElement={datePickerButtonRef.current}
            onCloseRequested={toggleDateFilterDialogVisibility}
            onChange={updateTimestamp}
            value={userSetFromTimestamp ?? today}
          />
        </div>
        <span
          css={css`
            line-height: 3;
            font-size: 13px;
            font-weight: 600;
          `}
        >
          {userSetFromTimestamp
            ? `${userSetFromTimestamp?.day}/${userSetFromTimestamp?.month + 1}`
            : null}
        </span>
      </Column>
      <Column
        css={css`
          justify-content: flex-end;
        `}
        width="30%"
      >
        <IconButton
          appearance="secondary"
          iconBefore={<PlayIcon />}
          onClick={() => startWorkflowRun(workflowName)}
          aria-label={translate('designer:runLog.runWorkflow')}
        />
        <IconButton
          appearance="secondary"
          iconBefore={<ToolbarRefreshIcon />}
          onClick={refreshHandler}
          aria-label={translate('designer:runLog.refresh')}
        />
      </Column>
    </Spacer>
  );
};
